import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from '../../../../components/Intl/withIntl.js';
import { Offcanvas, Button, Badge, Form } from 'react-bootstrap';

import { getProductAttributeList, getProductPrice } from '../../ProductActions.js';
import { getCountryName } from '../../../Intl/IntlHelper.js';
import { getUserGroup } from '../../../User/UserGroupActions.js';

import withRouter from '../../../../components/Router/WithRouter.js';
import Icon from '../../../../components/Content/Icon.js';

// Price ranges (base currency, tax incl.). Buckets that hold every / no product are hidden automatically.
const PRICE_BUCKETS = [
	{ id: '0-4', min: 0, max: 4 },
	{ id: '4-10', min: 4, max: 10 },
	{ id: '10-20', min: 10, max: 20 },
	{ id: '20-50', min: 20, max: 50 },
	{ id: '50', min: 50, max: Infinity },
];

// Weight ranges in grams (product.weight is stored in Kg). Empty / uniform buckets are hidden automatically.
const WEIGHT_BUCKETS = [
	{ id: '0-150', min: 0, max: 150 },
	{ id: '150-300', min: 150, max: 300 },
	{ id: '300-500', min: 300, max: 500 },
	{ id: '500', min: 500, max: Infinity },
];

function prettifyValue(value) {
	return `${value}`.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^\w/, char => char.toUpperCase());
}

function formatBucketBound(amount) {
	return `${Number.isInteger(amount) ? amount : amount.toFixed(2)} €`;
}

function priceBucketLabel(bucket, formatMessage) {
	if(bucket.min <= 0) {
		return formatMessage({ id: 'productFilterPriceUnder', defaultMessage: 'Under {max}' }, { max: formatBucketBound(bucket.max) });
	}
	if(!Number.isFinite(bucket.max)) {
		return formatMessage({ id: 'productFilterPriceOver', defaultMessage: 'Over {min}' }, { min: formatBucketBound(bucket.min) });
	}
	return `${formatBucketBound(bucket.min)} – ${formatBucketBound(bucket.max)}`;
}

function formatWeightBound(grams) {
	return grams >= 1000 ? `${(grams / 1000).toFixed(grams % 1000 === 0 ? 0 : 1)} kg` : `${grams} g`;
}

function weightBucketLabel(bucket, formatMessage) {
	if(bucket.min <= 0) {
		return formatMessage({ id: 'productFilterWeightUnder', defaultMessage: 'Under {max}' }, { max: formatWeightBound(bucket.max) });
	}
	if(!Number.isFinite(bucket.max)) {
		return formatMessage({ id: 'productFilterWeightOver', defaultMessage: 'Over {min}' }, { min: formatWeightBound(bucket.min) });
	}
	return `${formatWeightBound(bucket.min)} – ${formatWeightBound(bucket.max)}`;
}

// Each facet turns a product into the list of options it belongs to: { value (used for matching + url), label, sort? }.
// Options that all (or no) products share are dropped when building groups, so only facets that actually narrow are shown.
export const PRODUCT_FILTER_FACETS = [
	// {
	// 	key: 'brand',
	// 	labelId: 'productFilterBrand',
	// 	labelDefault: 'Brand',
	// 	getOptions: product => (product.brand ? [{ value: `${product.brand}`.trim(), label: `${product.brand}`.trim() }] : []),
	// },
	{
		key: 'benefit',
		labelId: 'productFilterBenefit',
		labelDefault: 'Benefits',
		getOptions: (product, ctx) => getProductAttributeList(product, 'benefits', ctx.locale)
			.filter(benefit => `${benefit}`.trim())
			.map(benefit => ({ value: `${benefit}`.trim().toLowerCase(), label: `${benefit}`.trim() })),
	},
	// {
	// 	key: 'label',
	// 	labelId: 'productFilterLabel',
	// 	labelDefault: 'Labels',
	// 	getOptions: product => (product.flags || [])
	// 		.filter(flag => `${flag}`.trim())
	// 		.map(flag => ({ value: `${flag}`.trim().toLowerCase(), label: prettifyValue(flag) })),
	// },
	// {
	// 	key: 'origin',
	// 	labelId: 'productFilterOrigin',
	// 	labelDefault: 'Origin',
	// 	getOptions: (product, ctx) => (product.countryOfOrigin
	// 		? [{ value: `${product.countryOfOrigin}`.toUpperCase(), label: getCountryName(product.countryOfOrigin, ctx.locale) }]
	// 		: []),
	// },
	{
		key: 'price',
		labelId: 'productFilterPrice',
		labelDefault: 'Price',
		getOptions: (product, ctx) => {
			const price = getProductPrice(product, 1, ctx.userGroup);
			const bucket = PRICE_BUCKETS.find(currentBucket => price >= currentBucket.min && price < currentBucket.max);
			return bucket ? [{ value: bucket.id, label: priceBucketLabel(bucket, ctx.formatMessage), sort: bucket.min }] : [];
		},
	},
	{
		key: 'weight',
		labelId: 'productFilterWeight',
		labelDefault: 'Weight',
		getOptions: (product, ctx) => {
			const grams = parseFloat(product.weight) * 1000;
			if(!Number.isFinite(grams) || grams <= 0) {
				return [];
			}
			const bucket = WEIGHT_BUCKETS.find(currentBucket => grams >= currentBucket.min && grams < currentBucket.max);
			return bucket ? [{ value: bucket.id, label: weightBucketLabel(bucket, ctx.formatMessage), sort: bucket.min }] : [];
		},
	},
	{
		key: 'promo',
		labelId: 'productFilterPromo',
		labelDefault: 'Offers',
		getOptions: (product, ctx) => (parseFloat(product.priceTTC) > getProductPrice(product, 1, ctx.userGroup) + 0.001
			? [{ value: 'promo', label: ctx.formatMessage({ id: 'productFilterPromoValue', defaultMessage: 'On sale' }) }]
			: []),
	},
];

// Read the selected filters from the url search params: { facetKey: [value, ...] }.
export function getActiveProductFilters(search) {
	const active = {};
	PRODUCT_FILTER_FACETS.forEach(facet => {
		const raw = search && search.get ? search.get(facet.key) : null;
		const values = (raw || '').split(',').filter(value => value);
		if(values.length) {
			active[facet.key] = values;
		}
	});
	return active;
}

// Keep products matching every active facet (OR within a facet, AND across facets).
export function applyProductFilters(products, active, ctx) {
	const activeKeys = Object.keys(active || {});
	if(!activeKeys.length) {
		return products;
	}
	return products.filter(product => activeKeys.every(key => {
		const facet = PRODUCT_FILTER_FACETS.find(currentFacet => currentFacet.key === key);
		if(!facet) {
			return true;
		}
		const optionValues = facet.getOptions(product, ctx).map(option => `${option.value}`);
		return active[key].some(value => optionValues.includes(`${value}`));
	}));
}

// Aggregate the products into displayable facet groups, dropping options shared by all (or no) products.
function buildFacetGroups(products, ctx) {
	const total = products.length;
	return PRODUCT_FILTER_FACETS
		.map(facet => {
			const optionsByValue = new Map();
			products.forEach(product => {
				const seen = new Set();
				facet.getOptions(product, ctx).forEach(option => {
					const value = `${option.value}`;
					if(seen.has(value)) {
						return;
					}
					seen.add(value);
					const existing = optionsByValue.get(value) || { value, label: option.label, sort: option.sort, count: 0 };
					existing.count += 1;
					optionsByValue.set(value, existing);
				});
			});
			const options = Array.from(optionsByValue.values()).filter(option => option.count > 0 && option.count < total);
			if(facet.key === 'price' || facet.key === 'weight') {
				options.sort((optionA, optionB) => (optionA.sort || 0) - (optionB.sort || 0));
			} else {
				options.sort((optionA, optionB) => optionB.count - optionA.count || `${optionA.label}`.localeCompare(`${optionB.label}`));
			}
			return { ...facet, options };
		})
		.filter(group => group.options.length > 0);
}

function ProductsFilters({ intl, search, navigate, location, products = [], userGroup = null }) {
	const [show, setShow] = useState(false);

	const ctx = { locale: intl.locale, userGroup, formatMessage: intl.formatMessage };
	const groups = buildFacetGroups(products, ctx);
	const active = getActiveProductFilters(search);
	const activeCount = Object.values(active).reduce((sum, values) => sum + values.length, 0);

	if(!groups.length) {
		return null;
	}

	const resultCount = applyProductFilters(products, active, ctx).length;

	// Navigate client-side (no reload) so the list updates live. Buttons are used (not <a href>), so no crawlable
	// faceted urls are emitted; SeoHead already sets a canonical to the clean path whenever a query param is present.
	const goTo = params => {
		params.delete('page'); // Filtering changes the result set, so restart pagination.
		const queryString = params.toString();
		navigate(queryString ? `?${queryString}` : location.pathname);
	};

	const toggle = (key, value) => {
		const params = new URLSearchParams(search);
		const current = (params.get(key) || '').split(',').filter(item => item);
		const next = current.includes(value) ? current.filter(item => item !== value) : [...current, value];
		if(next.length) {
			params.set(key, next.join(','));
		} else {
			params.delete(key);
		}
		goTo(params);
	};

	const reset = () => {
		const params = new URLSearchParams(search);
		PRODUCT_FILTER_FACETS.forEach(facet => params.delete(facet.key));
		goTo(params);
	};

	return (
		<React.Fragment>
			<Button variant="outline-light" size="sm" className="category-filter text-dark" onClick={() => setShow(true)}>
				<Icon icon="list" />{' '}
				<FormattedMessage id="productFilterButton" defaultMessage="Filter" />
				{activeCount > 0 && <Badge bg="danger" pill className="ms-2">{activeCount}</Badge>}
			</Button>

			<Offcanvas show={show} onHide={() => setShow(false)} placement="end" aria-labelledby="products-filters-title">
				<Offcanvas.Header closeButton>
					<Offcanvas.Title id="products-filters-title">
						<FormattedMessage id="productFilterTitle" defaultMessage="Filters" />
						{activeCount > 0 && <Badge bg="danger" pill className="ms-2">{activeCount}</Badge>}
					</Offcanvas.Title>
				</Offcanvas.Header>
				<Offcanvas.Body>
					{groups.map(group => (
						<div key={group.key} className="mb-4">
							<h6 className="text-uppercase text-muted border-bottom pb-2 mb-2">
								<FormattedMessage id={group.labelId} defaultMessage={group.labelDefault} />
							</h6>
							{group.options.map(option => (
								<Form.Check
									key={option.value}
									type="checkbox"
									id={`filter-${group.key}-${option.value}`}
									className="mb-1"
									checked={(active[group.key] || []).includes(option.value)}
									onChange={() => toggle(group.key, option.value)}
									label={<span>{option.label} <span className="text-muted small">({option.count})</span></span>}
								/>
							))}
						</div>
					))}
				</Offcanvas.Body>
				<div className="d-flex gap-2 p-3 border-top">
					{activeCount > 0 && (
						<Button variant="outline-secondary" className="flex-shrink-0" onClick={reset}>
							<FormattedMessage id="productFilterReset" defaultMessage="Reset" />
						</Button>
					)}
					<Button variant="danger" className="flex-grow-1" onClick={() => setShow(false)}>
						<FormattedMessage id="productFilterApply" defaultMessage="See {count} products" values={{ count: resultCount }} />
					</Button>
				</div>
			</Offcanvas>
		</React.Fragment>
	);
}

function mapStateToProps(store) {
	return {
		userGroup: getUserGroup(store),
	};
}

ProductsFilters.propTypes = {
	intl: PropTypes.object.isRequired,
	search: PropTypes.object.isRequired,
	navigate: PropTypes.func.isRequired,
	location: PropTypes.object.isRequired,
	products: PropTypes.arrayOf(PropTypes.object),
	userGroup: PropTypes.object,
};

export default connect(mapStateToProps)(withRouter(injectIntl(ProductsFilters), ['search', 'navigate', 'location']));
